import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { types as coreTypes } from '@sitebuilder/core';
import { PublicComponentApiClient } from '../api/publicComponentsApiClient';
import { ThunkResult } from './index';

export interface StateShape {
    component: coreTypes.ReferenceComponent | null;
    isComponentLoaded: boolean;
    componentLoading: boolean;
    componentLoadError: any | null;
    components: [coreTypes.ReferenceComponent] | null;
    componentsLoading: boolean;
    componentsLoadError: any | null;
}

export const publicComponentsDefaultState: StateShape = {
    component: null,
    isComponentLoaded: false,
    componentLoading: false,
    componentLoadError: null,
    components: null,
    componentsLoading: false,
    componentsLoadError: null,
};

const apiClient = new PublicComponentApiClient();

export const actionCreators = {
    loadComponent: (
        libraryName: string,
        componentId: string
    ): ThunkResult<Promise<coreTypes.ReferenceComponent | undefined>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(publicComponentsReducerActions.COMPONENT_LOAD_START());

            try {
                const component = await apiClient.getPublicComponent(libraryName, componentId, {
                    accessToken,
                });
                dispatch(publicComponentsReducerActions.COMPONENT_LOAD_END({ component }));

                return component;
            } catch (err: any) {
                console.error(err);
                dispatch(publicComponentsReducerActions.SET_IS_COMPONENT_LOADED(true));
                dispatch(
                    publicComponentsReducerActions.COMPONENT_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },

    listComponents: (
        libraryName: string,
        folderName: string,
        filters: { idPattern?: string[]; authorsIn?: Record<string, unknown> | [] } = {
            idPattern: [],
            authorsIn: [],
        },
        sort?: { field?: string; dir?: string },
        take?: number,
        skip?: number
    ): ThunkResult<Promise<void>> => {
        return async (dispatch, getState) => {
            const state = getState();
            const accessToken = state.auth.user?.access_token;
            dispatch(publicComponentsReducerActions.COMPONENTS_LOAD_START());

            try {
                const components = await apiClient.listPublicComponents(
                    libraryName,
                    folderName,
                    state.components.showDeletedItems,
                    {
                        accessToken,
                    },
                    filters,
                    sort,
                    take,
                    skip
                );
                dispatch(publicComponentsReducerActions.COMPONENTS_LOAD_END({ components }));
            } catch (err: any) {
                console.error(err);
                dispatch(
                    publicComponentsReducerActions.COMPONENTS_LOAD_END({
                        error: { message: err.message },
                    })
                );
            }
        };
    },
};

const publicComponentsSlice = createSlice({
    name: 'publicComponents',
    initialState: publicComponentsDefaultState,
    reducers: {
        COMPONENT_LOAD_START(draft) {
            draft.componentLoading = true;
            draft.component = null;
            draft.componentLoadError = null;
        },
        COMPONENT_LOAD_END(
            draft,
            action: PayloadAction<{ component?: coreTypes.ReferenceComponent; error?: any }>
        ) {
            const { component, error } = action.payload;
            draft.componentLoading = false;
            draft.component = component || null;
            draft.componentLoadError = error;
        },
        SET_IS_COMPONENT_LOADED(draft, action: PayloadAction<boolean>) {
            draft.isComponentLoaded = action.payload;
        },
        COMPONENTS_LOAD_START(draft) {
            draft.componentsLoading = true;
            draft.components = null;
            draft.componentsLoadError = null;
        },
        COMPONENTS_LOAD_END(
            draft,
            action: PayloadAction<{ components?: [coreTypes.ReferenceComponent]; error?: any }>
        ) {
            const { components, error } = action.payload;
            draft.componentsLoading = false;
            draft.components = components || null;
            draft.componentsLoadError = error;
        },
    },
});

const publicComponentsReducerActions = publicComponentsSlice.actions;
export const publicComponentsReducer = publicComponentsSlice.reducer;
